if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (!this.form) return;

      this.idInput = this.form.querySelector('[name="id"]');
      if (this.idInput) this.idInput.disabled = false;

      this.qtyInput = this.form.querySelector('[name="quantity"]');

      this.cartDrawer = document.querySelector('cart-drawer');
      this.submitBtn = this.querySelector('[name="add"]');

      // Scope used for variant-picker lookup (nearest section is safest)
      this.scope =
        this.closest('.shopify-section') ||
        this.closest('section') ||
        document;

      // Load variants from <variant-picker> JSON
      this.variants = this.getVariantsJson();

      // Keep size table variant ids in sync with current selected variant (color etc.)
      this.bindVariantSyncEvents();
      this.updateSizeTableVariantIds(); // initial

      if (theme.settings.afterAtc !== 'no-js') {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }
    }

    // -----------------------------
    // Variant JSON / Sync
    // -----------------------------

    getVariantsJson() {
      let vp = this.scope.querySelector('variant-picker');
      if (!vp) vp = document.querySelector('variant-picker');
      if (!vp) return [];

      const jsonScript = vp.querySelector('script[type="application/json"]');
      if (!jsonScript) return [];

      try {
        const parsed = JSON.parse(jsonScript.textContent);
        return parsed?.product?.variants || [];
      } catch (e) {
        console.warn('Failed to parse variant-picker JSON', e);
        return [];
      }
    }

    bindVariantSyncEvents() {
      const handler = () => this.updateSizeTableVariantIds();

      if (this.idInput) {
        this.idInput.addEventListener('change', handler);
        this.idInput.addEventListener('input', handler);
      }

      this.form.addEventListener('change', (e) => {
        if (e.target && e.target.closest('.product__option, .variant-picker, .swatch, [data-option]')) {
          this.updateSizeTableVariantIds();
        }
      });
    }

    getSelectedOptionsFromForm() {
      const currentId = Number(this.form.querySelector('[name="id"]')?.value || 0);
      const v = this.variants.find((x) => Number(x.id) === currentId);
      return v?.options ? v.options.slice() : [];
    }

    updateSizeTableVariantIds() {
      if (!this.variants || !this.variants.length) return;

      const selectedOptions = this.getSelectedOptionsFromForm();
      if (!selectedOptions || !selectedOptions.length) return;

      const fieldsets = Array.from(
        document.querySelectorAll('fieldset.size-qty-table[data-selector-type="size-qty-list"]')
      );
      if (!fieldsets.length) return;

      fieldsets.forEach((fs) => {
        const sizeIndex0 = (parseInt(fs.dataset.optionPosition, 10) || 1) - 1;

        const rows = Array.from(fs.querySelectorAll('[data-size-input-row]'));
        rows.forEach((row) => {
          const sizeValue = row.getAttribute('data-size-value') || '';
          if (!sizeValue) return;

          const desired = selectedOptions.slice();
          desired[sizeIndex0] = sizeValue;

          const match = this.variants.find((v) => {
            if (!v.options || v.options.length !== desired.length) return false;
            for (let i = 0; i < desired.length; i++) {
              if (String(v.options[i]) !== String(desired[i])) return false;
            }
            return true;
          });

          if (match?.id) {
            row.dataset.variantId = match.id;

            const qtyInput = row.querySelector('input[data-size-qty]');
            if (qtyInput) {
              qtyInput.dataset.variantId = match.id;
              qtyInput.dataset.variantTitle = match.title || '';
            }

            const unitPriceEl = row.querySelector('[data-unit-price]');
            if (unitPriceEl && typeof match.price === 'number') {
              try {
                if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
                  unitPriceEl.textContent = Shopify.formatMoney(match.price, theme.moneyFormat);
                } else if (window.Shopify?.formatMoney && window.theme?.settings?.moneyFormat) {
                  unitPriceEl.textContent = Shopify.formatMoney(match.price, theme.settings.moneyFormat);
                }
              } catch (e) {}
            }
          } else {
            row.removeAttribute('data-variant-id');
            delete row.dataset.variantId;

            const qtyInput = row.querySelector('input[data-size-qty]');
            if (qtyInput) {
              qtyInput.removeAttribute('data-variant-id');
              delete qtyInput.dataset.variantId;
            }

            const unitPriceEl = row.querySelector('[data-unit-price]');
            if (unitPriceEl) unitPriceEl.textContent = '—';
          }
        });
      });
    }

    // -----------------------------
    // Helpers for line item payload
    // -----------------------------

    getPropertiesFromFormData(formData) {
      const props = {};
      for (const [key, value] of formData.entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (!m) continue;
        const propName = m[1];
        if (value === '' || value == null) continue;
        props[propName] = value;
      }
      return props;
    }

    /**
     * IMPORTANT: size table is outside of form, so query document.
     * Returns normalized lines: [{ id: Number, qty: Number }]
     */
    getSizeLines() {
      const fieldsets = Array.from(
        document.querySelectorAll('fieldset.size-qty-table[data-selector-type="size-qty-list"]')
      );
      const inputs = fieldsets.flatMap((fs) => Array.from(fs.querySelectorAll('input[data-size-qty]')));

      const lines = inputs
        .map((el) => {
          const qty = parseInt(el.value, 10) || 0;

          const rawId =
            el.dataset.variantId ||
            el.dataset.variant ||
            el.dataset.id ||
            el.getAttribute('data-variant-id') ||
            el.getAttribute('data-variant') ||
            el.getAttribute('data-id') ||
            '';

          const id = Number(rawId || 0);
          return { el, id, qty };
        })
        .filter((x) => x.id && x.qty > 0);

      return lines;
    }

    /**
     * NEW: if all size inputs are empty/0, show message + scroll to table.
     */
    requireSizeQtyOrPrompt() {
      const fieldset =
        document.querySelector('fieldset.size-qty-table[data-selector-type="size-qty-list"]') || null;

      // If size table doesn't exist on this template, don't block ATC
      if (!fieldset) return true;

      const inputs = Array.from(fieldset.querySelectorAll('input[data-size-qty]'));
      // If no inputs, don't block ATC
      if (!inputs.length) return true;

      const total = inputs.reduce((sum, el) => sum + (parseInt(el.value, 10) || 0), 0);

      if (total > 0) return true;

      // Dialog / message
      const msg = 'Please add a quantity for at least one size.';
      if (typeof window.showDialog === 'function') {
        // If your theme has a dialog helper, it’ll use it
        window.showDialog(msg);
      } else if (typeof window.theme?.dialog === 'function') {
        window.theme.dialog(msg);
      } else {
        alert(msg);
      }

      // Scroll to size table
      fieldset.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Optional: focus first input for convenience
      const first = inputs[0];
      if (first) {
        setTimeout(() => {
          try {
            first.focus({ preventScroll: true });
          } catch (e) {
            first.focus();
          }
        }, 350);
      }

      return false;
    }

    // -----------------------------
    // Submit / ATC
    // -----------------------------

    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;
      evt.preventDefault();

      // NEW: block ATC if size table exists AND all qty are 0/empty
      if (!this.requireSizeQtyOrPrompt()) return;

      if (this.submitBtn?.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      this.submitBtn?.setAttribute('aria-disabled', 'true');
      this.submitBtn?.classList.add('is-loading');

      // Ensure size table has latest ids before reading quantities
      this.updateSizeTableVariantIds();

      const formData = new FormData(this.form);
      formData.append('sections_url', window.location.pathname);

      const ADDON_MULT_BY_MAIN_VARIANT = {
        50028456476919: 100,
        50028456509687: 200,
        50044048212215: 200,
        50028456542455: 360,
        50044048244983: 360,
        50044059877623: 360,
        50028456575223: 720,
        50044048277751: 720,
        50044059910391: 720,
        50044060532983: 720
      };
      const getMultiplier = (variantId) => ADDON_MULT_BY_MAIN_VARIANT[variantId] || 1;

      const sizeLines = this.getSizeLines();
      const isMultiSizeAdd = sizeLines.length > 0;

      const sharedProperties = this.getPropertiesFromFormData(formData);

      const prevIdDisabled = this.idInput ? this.idInput.disabled : null;
      const prevQtyDisabled = this.qtyInput ? this.qtyInput.disabled : null;

      try {
        let mainData = null;

        // =========================
        // 1) ADD MAIN PRODUCT(S)
        // =========================
        if (!isMultiSizeAdd) {
          if (this.idInput) this.idInput.disabled = false;

          const mainResponse = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData
          });

          mainData = await mainResponse.json();
          if (!mainResponse.ok) throw new Error(mainData.message || 'Main product error');
        } else {
          if (this.idInput) this.idInput.disabled = true;
          if (this.qtyInput) this.qtyInput.disabled = true;

          const items = sizeLines.map((x) => ({
            id: x.id,
            quantity: x.qty,
            properties: sharedProperties
          }));

          const sections = formData.getAll('sections');
          const payload = { items, sections_url: window.location.pathname };
          if (sections && sections.length) payload.sections = sections;

          const multiMainResponse = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload)
          });

          mainData = await multiMainResponse.json();
          if (!multiMainResponse.ok) throw new Error(mainData.message || 'Main product error');
        }

        // =========================
        // 2) BUILD ADD-ONS
        // =========================
        const addons = [];

        const checkedSides = Array.from(this.form.querySelectorAll('.logo-position-checkbox:checked'));
        const selectedSidesCount = checkedSides.length;

        let scaledMainQty = 0;

        if (!isMultiSizeAdd) {
          const mainQty = parseInt(formData.get('quantity'), 10) || 1;
          const mainVariantId = Number(formData.get('id'));
          scaledMainQty = mainQty * getMultiplier(mainVariantId);
        } else {
          scaledMainQty = sizeLines.reduce((sum, x) => sum + x.qty * getMultiplier(x.id), 0);
        }

        checkedSides.forEach((cb) => {
          const sideVar = Number(cb.dataset.variant);
          const sidePos = cb.dataset.position || '';
          if (sideVar) {
            addons.push({
              id: sideVar,
              quantity: Math.max(1, scaledMainQty),
              properties: { 'Logo Position': sidePos }
            });
          }
        });

        if (selectedSidesCount > 0) {
          const applyLogoRadio = this.form.querySelector('input[name="properties[Apply Logo]"]:checked');
          if (applyLogoRadio?.dataset?.variant) {
            const applyVar = Number(applyLogoRadio.dataset.variant);
            if (applyVar) {
              addons.push({
                id: applyVar,
                quantity: Math.max(1, scaledMainQty * selectedSidesCount)
              });
            }
          }

          const vinylRadio = this.form.querySelector('input[name="properties[Logo Vinyl Color]"]:checked');
          if (vinylRadio?.dataset?.variant) {
            const vinylVar = Number(vinylRadio.dataset.variant);
            if (vinylVar) {
              addons.push({
                id: vinylVar,
                quantity: Math.max(1, scaledMainQty * selectedSidesCount)
              });
            }
          }

          const COLOR_VARIANT_FALLBACK = {
            '2': 50144222052599,
            '3': 50144222085367,
            '4': 50144222118135
          };

          const colorsChoice = this.form.querySelector('input[name="properties[Logo Colors]"]:checked');
          if (colorsChoice && colorsChoice.value !== '1') {
            const colorVariantId = colorsChoice.dataset?.variant
              ? Number(colorsChoice.dataset.variant)
              : (COLOR_VARIANT_FALLBACK[colorsChoice.value] || null);

            if (colorVariantId) {
              addons.push({
                id: colorVariantId,
                quantity: Math.max(1, scaledMainQty * selectedSidesCount)
              });
            }
          }
        }

        (() => {
          const selectEl = this.form.querySelector('#stencil-option');
          if (!selectEl || selectEl.value === 'none') return;

          const stencilVarId = (this.form.querySelector('#stencil-variant-id')?.value || '').trim();
          if (!stencilVarId) return;

          addons.push({
            id: Number(stencilVarId),
            quantity: Math.max(1, scaledMainQty)
          });
        })();

        // =========================
        // 3) ADD ALL ADD-ONS
        // =========================
        if (addons.length) {
          const addonResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: addons })
          });

          const addonData = await addonResponse.json();
          if (!addonResponse.ok) throw new Error(addonData.message || 'Addon product error');
        }

        // =========================
        // 4) POST-ATC BEHAVIOR
        // =========================
        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          if (mainData?.sections?.['cart-icon-bubble']) {
            ProductForm.updateCartIcon(mainData);
          }

          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(mainData, !quickAddDrawer && theme.settings.afterAtc === 'drawer');
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) cartItems.refresh();
            }
          }, 700);
        }
      } catch (err) {
        console.error('ATC error', err);
        this.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: this.errorMsg?.textContent }
          })
        );
        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        if (this.idInput && prevIdDisabled !== null) this.idInput.disabled = prevIdDisabled;
        if (this.qtyInput && prevQtyDisabled !== null) this.qtyInput.disabled = prevQtyDisabled;

        this.submitBtn?.classList.add('is-success');
        this.submitBtn?.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn?.classList.remove('is-loading', 'is-success');
        }, 1400);
      }
    }

    // -----------------------------
    // Theme utilities
    // -----------------------------

    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response?.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}